import { CommonModule } from "@angular/common";
import {
  Component,
  inject,
  Input,
  OnInit,
  signal
} from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder
} from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { FUNCTION_RESULT_STATUS } from "@nova/common-interfaces";
import { ICustomerOverview, ReferenceKeyHelper } from "@rfm/shared-interfaces";
import {
  injectStripe
} from "ngx-stripe";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { InputNumberModule } from "primeng/inputnumber";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "../../../../../../environments/environment";
import { CustomerService } from "../../../../../shared/services/customers/customers.service.mjs";
import { PaymentService } from "../../../../../shared/services/financial/payment.service.mjs";
import { ThirdPartyStripeService } from "../../../../../shared/services/third-party/stripe/third-party-stripe.service";

@Component({
  selector: "app-stripe-default-card-checkout-form",
  templateUrl: "./stripe-payment-default.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    InputNumberModule,
    ToggleSwitchModule,
    ButtonModule,
  ],
})
export class ThirdpartyStripeDefaultCardCheckoutFormComponent
  implements OnInit
{
  // Replace with your own public key
  stripe = injectStripe(environment.stripeApiKey);
  paying = signal(false);
  checked = true;
  stripePaymentComplete = false;
  client_secret: string | undefined;
  payment_method = "";

  private _isLoading = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this._isLoading.asObservable();

  @Input()
  customer_uid = signal("");

  @Input()
  payment_amount = signal(0);

  customer_overview: ICustomerOverview | undefined;

  toggleSwitchChanged(event: any) {
    console.log(event);
  }

  constructor(
    protected thirdPartyStripeService: ThirdPartyStripeService,
    config: DynamicDialogConfig,
    protected ref: DynamicDialogRef,
    protected customerService: CustomerService,
    protected paymentService: PaymentService
  ) {
    this.customer_uid.set(config.data["uid"]);
  }

  ngOnInit(): void {
    this._isLoading.next(true);

    this.customerService
      .getOverview(this.customer_uid())
      .then(async (result) => {
        if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
          console.log("ERROR GETTING CUSTOMER FOR STRIPE PAYMENT");
          return Promise.reject("ERROR GETTING CUSTOMER");
        }

        this.customer_overview = result.data!;

        this.payment_amount.set(
          this.customer_overview!.balances?.data.reduce(
            (accumulator: any, current: any) =>
              accumulator + current.current_balance,
            0
          )
        );

        if (!environment.production && this.payment_amount() === 0) {
          this.payment_amount.set(101);
        }

        await this.setupPaymentIntent(this.payment_amount());
      });
  }

  modelChanged() {
    if (this.payment_amount() > 0) {
    }
  }

  getButtonLabel(): string {
    return `Pay $ ${(this.payment_amount() as number).toFixed(2)} Now`;
  }

  async setupPaymentIntent(amount: number) {
    const stripeId = ReferenceKeyHelper.getId(
      "stripe",
      this.customer_overview!.reference_keys.data
    );

    console.log("STRIPE ID", stripeId);

    const paymentMethodResult = await this.thirdPartyStripeService.getPaymentMethods(stripeId!)

    const paymentMethods = paymentMethodResult.data;

    if (paymentMethods?.length === 0)
    {
      throw new Error("Cannot find payment method");
    }

    const defaultPaymentMethods = paymentMethods?.filter(x => x.isDefault);

    if (!defaultPaymentMethods)
    {
      this.payment_method = paymentMethods![0].id;
    }
    else
    {
      this.payment_method = defaultPaymentMethods[0].id;
    }

    if (!this.payment_method || this.payment_method.length === 0)
    {
      throw new Error("Cannot find payment method");
    }

    this.thirdPartyStripeService
      .createPaymentIntent(
        stripeId!,
        amount * 100, //stripe requires amount to be sent in cents
        "usd",
        this.checked,
        this.payment_method
      )
      .then((result) => {
        if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
          console.error("GETTING CLIENT SECRET FAILED", result);
          return Promise.reject("UNABLE TO GET CLIENT SECRET");
        }
        this.client_secret = result.data!.client_secret!;
        return Promise.resolve();
      })
      .finally(() => {
        this._isLoading.next(false);
      });
  }

  fb = inject(UntypedFormBuilder);

  async pay() {
    try {
      this.paying.set(true);

      console.log("confirming payment");
      this.stripe
        .confirmCardPayment(this.client_secret!, {
          payment_method: this.payment_method
        })
        .subscribe(async (result) => {
          if (result.error) {
            // Show error to your customer (e.g., insufficient funds)
            alert({ success: false, error: result.error.message });
          } else {
            this.stripePaymentComplete = true;
            // The payment has been processed!
            if (
              result.paymentIntent.status === "succeeded" ||
              result.paymentIntent.status === "processing"
            ) {
              await this.paymentService.addPaymentIntent(
                result.paymentIntent.id,
                this.customer_overview!
              );
              this.ref.close(result.paymentIntent);
              // Show a success message to your customer
            }
          }
          this.paying.set(false);
        });
    } catch (error) {
      console.error(error);
      this.paying.set(false);
      if (this.stripePaymentComplete) {
        this.ref.close(undefined);
      }
    }
  }

  cancel() {
    this.ref.close();
  }
}
